import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  withCredentials: true,
  baseURL: API_URL,
  timeout: 10000,
});

const handleUnauthorized = () => {
  // Clear user data from local storage if needed
  localStorage.clear();
  window.location.href = '/login'; // Redirect to login page
};

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const currentPath = window.location.pathname;
      // console.log('response 401 error:', error.response.data);
      // DEVELOPER NOTE: This line of code is for the change password modal when we check if the current
      // password is incorrect. We don't want to redirect to the login page in this case.
      if (
        error.response.data === 'Incorrect password'||
        error.response.config.url === '/canva/authenticate'
      ) {
        return Promise.reject(error); // Let component handle this error
      }

      // Handle general unauthorized errors (e.g., session expiration)
      if (
        currentPath !== '/login' &&
        currentPath !== '/sso/cms' &&
        currentPath !== '/sso/cms-new' &&
        currentPath !== '/sso'
      ) {
        handleUnauthorized();
      }
    }
    return Promise.reject(error);
  }
);

const API = {
  // LOGIN, AUTHENTICATE, AND REGISTRATION

  /**
   * Login API userInfo consists of email, password
   * @param {*} userInfo 
   * @returns 
   */
  login: function (userInfo) {
    return axiosInstance.post('/auth/login', userInfo)
  },
  authenticate: function () {
    return axiosInstance.get('/auth/authenticate')
  },
  logout: function () {
    // Update last seen status before logging out to ensure it's marked as offline
    return axiosInstance.put('/user/last-seen')
      .then(() => {
        return axiosInstance.post('/auth/logout')
      })
      .catch(error => {
        // If updating last seen fails, still try to logout
        console.error('Error updating last seen status during logout:', error);
        return axiosInstance.post('/auth/logout');
      });
  },
  register: function (userInfo) {
    return axiosInstance.post('/auth/registration', userInfo)
  },
  updateUserLastSeen: function () {
    return axiosInstance.put('/user/last-seen')
  },
  // USERS
  activateUser: function (email, passcode) {
    return axiosInstance.post('/user/activate', {
      email,
      passcode
    })
  },
  getAllUsers: function () {
    return axiosInstance.get("/user/all", { timeout: 60000 })
  },
  getUserById: function(id) {
    return axiosInstance.get(`/user/id/${id}`)
  },
  // userInfo contains email, firstName, lastName, and phone
  addUser: function (userInfo) {
    return axiosInstance.post("/user/", userInfo)
      .then(response => {
        console.log('API addUser response:', response)
        return response
      })
      .catch(error => {
        console.log('API addUser error:', error)
        throw error
      })
  },
  addUserRole: function (userId, roleId, settings) {
    return axiosInstance.post('/user/role', {
      userId,
      roleId,
      settings,
    })
  },
  // userInfo contains email, firstName, lastName, phone, and id
  editUser: function (userInfo) {
    if (userInfo.phone === undefined) {
      userInfo.phone = null;
    }
    return axiosInstance.put("/user/", userInfo)
  },
  deleteUserRole: function (userInfo) {
    return axiosInstance.delete('/user/role', { data: userInfo });
  },
  deleteUser: function (id) {
    return axiosInstance.delete('/user/', {
      data: { id }
    });
  },
  setRoles: function (users, roles, type) {
    return axiosInstance.put('/user/roles', { users, roles, type})
  },

  // ALERT PLUS
  addAlertPlusUser: function (userInfo) {
    return axiosInstance.post("/aplus/user", userInfo)
  },
  getAlertPlusUsers: function () {
    return axiosInstance.get('/aplus/users');
  },
  getSites: function () {
    return axiosInstance.get('/aplus/sites')
  },
  getSiteAlerts: function (siteId) {
    return axiosInstance.get(`/aplus/site/${siteId}`)
  },
  getChildSites: function (siteId, cmsId) {
    return axiosInstance.get(`/aplus/site/${siteId}/children?cmsId=${cmsId}`)
  },
  getAlerts: function () {
    return axiosInstance.get('/aplus/alerts')
  },
  getRaptorAlerts: function() {
    return axiosInstance.get('/aplus/raptor/alerts')
  },
  getTeacherStatus: function(alertId) {
    return axiosInstance.get(`/aplus/teacher/status/${alertId}`)
  },
  triggerAlert: function (siteIds, templateId) {
    return axiosInstance.post('/aplus/alert', { siteIds, templateId })
  },
  clearAlert: function (siteId) {
    return axiosInstance.post('/aplus/clear', { siteId })
  },
  dismissAlert: function (siteId, templateId) {
    return axiosInstance.post('/aplus/dismiss', { siteId, templateId })
  },
  getAplusMode: function () {
    return axiosInstance.get('/aplus/mode')
  },
  putAplusMode: function (mode) {
    return axiosInstance.put('/aplus/mode', { mode })
  },
  putAplusUsers: function (cmsId, siteIds, userIds) {
    return axiosInstance.put('/aplus/users', { cmsId, siteIds, userIds })
  },


  //Screenbeam Message Manager
  putAssignSitesForMsgManagerUsr: function(cmsId, /*featureIds,*/ siteIds, userIds) {
    return axiosInstance.put('/messenger/users/sites', {cmsId, siteIds, userIds})
  },
  //Screenbeam Signage Plus
   putAssignSitesForSignagePlusUsr: function(cmsId, /*featureIds,*/ siteIds, userIds) {
    return axiosInstance.put('/signage/users/sites', {cmsId, siteIds, userIds})
  },
  getRaptorBuildings: async (clientUID) => {
    return axiosInstance.get(`/aplus/raptor/buildings/${clientUID}`);
  },

  // RAPTOR CLIENT UID
  updateRaptorClientUID: function(clientUID) {
    return axiosInstance.post('/account/raptor-client-uid', { clientUID });
  },
  getRaptorClientUID: function() {
    return axiosInstance.get('/account/raptor-client-uid')
  },

  // GLOBAL POLICIES
  getGlobalPolicies: function () {
    return axiosInstance.get('/account/policy')
  },
  getEmailGlobalPolicies: function (email) {
    return axiosInstance.get(`/user/${email}/policy`)
  },
  updateGlobalPolicy: function (policyID, settings) {
    return axiosInstance.put('/account/policy', {
      policyID, settings
    })
  },
  deleteGlobalPolicy: function (policyID) {
    return axiosInstance.delete('/account/policy', {
      data: { policyID }
    })
  },

  // AUTH VERIFY AND PASSWORD RESET
  verifyEmail: function (email, passcode) {
    return axiosInstance.post('/auth/verify', { email, passcode })
  },
  emailResetPassword: function (email) {
    return axiosInstance.post('/auth/password/reset/', email)
  },
  resetPassword: function (userInfo) {
    return axiosInstance.post('/auth/password/reset/verify', userInfo)
  },
  changePassword: function (oldPassword, newPassword) {
    return axiosInstance.post('/user/password', { oldPassword, newPassword })
  },
  resendVerification: function (email) {
    return axiosInstance.post('/auth/verify/resend', { email })
  },
  resendUserVerification: function (email) {
    return axiosInstance.post('/user/verify/resend', { email })
  },

  resendVerifyForCMSECreateAccount: function (email) {
    return axiosInstance.post('/auth/verify/resend', { email })
  },

  // LICENSING
  getAccountLicenses: function () {
    return axiosInstance.get('/account/licenses')
  },
  addAccountLicense: function () {
    return axiosInstance.post('/account/licenses')
  },

  // CMS
  getCms: function (cmsId) {
    return axiosInstance.get(`/cms/${cmsId}`)
  },
  getCmses: function () {
    return axiosInstance.get('/cms/')
  },
  editCms: function (id, name) {
    return axiosInstance.get('/cms', { id, name })
  },
  deleteCmses: function (ids) {
    return axiosInstance.delete('/cms/', { data: { ids } })
  },
  registerCms: function (email, password, name, socketID) {
    return axiosInstance.post('/cms/register', { email, password, name, socketID })
  },
  loginCms: function (email, password, cmsId, socketID) {
    return axiosInstance.post('/cms/login', { email, password, cmsId, socketID })
  },
  getCmsCounts: function () {
    return axiosInstance.get('/cms/count/all');
  },
  registerCCServer: function (email, password, name, socketID) {
    return axiosInstance.post('/cc-server/register', { email, password, name, socketID })
  },
  loginCCServer: function (email, password, ccServerId, socketID) {
    return axiosInstance.post('/cc-server/login', { email, password, ccServerId, socketID })
  },
  getCCServer: function (ccId) {
    return axiosInstance.get(`/cc-server/${ccId}`)
  },
  // HARDWARE WARRANTY
  getReceivers: function () {
    return axiosInstance.get('/receiver/all', { timeout: 120000 })
  },
  getAccountReceivers: function (accountId) {
    return axiosInstance.get(`/receiver?accountId=${accountId}`)
  },
  getReceiversByCmsId: function (cmsId) {
    return axiosInstance.get(`/receiver/cms/${cmsId}`)
  },
  getReceiverModelCounts: function () {
    return axiosInstance.get('/receiver/model/count');
  },
  // ROLES
  getRoles: function () {
    return axiosInstance.get('/role/all')
  },
  addRole: function (permissions, roleName, description, settings) {
    return axiosInstance.post('/role/', { permissions, roleName, description, settings })
  },
  editRole: function (id, permissions, roleName, description, settings) {
    return axiosInstance.put('/role/', { id, permissions, roleName, description, settings })
  },
  deleteRole: function (id) {
    return axiosInstance.delete('/role', { data: { id } })
  },
  getPermissions: function () {
    return axiosInstance.get('/role/permission/all')
  },
  addPermission: function (permissionName) {
    return axiosInstance.post('/role/permission', { permissionName })
  },
  deletePermission: function (permissionID) {
    return axiosInstance.delete('/role/permission', { data: { permissionID } })
  },

  //Soraco commands require a longer timeout, they should all be paired with UX
  //methods to alleviate the issue
  refreshLicenses: function () {
    return axiosInstance.get('/license/refresh', { timeout: 30000 })
  },
  getLicenses: function () {
    return axiosInstance.get('/license/all')
  },
  activateLicense: function (key) {
    return axiosInstance.post('/license', { key }, { timeout: 30000 })
  },
  deactivateLicense: function (key) {
    return axiosInstance.delete('/license', { data: { key }, timeout: 30000 })
  },
  importUsers: function (file) {
    let formData = new FormData();
    formData.set("users", file);
    return axiosInstance.post('/user/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  importReceivers: function (file, accountId) {
    let formData = new FormData();
    formData.set("rx", file);
    formData.set("accountId", accountId)
    return axiosInstance.post('/receiver/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 90000
    });
  },
  importUsersSupport: function (file) {
    let formData = new FormData();
    formData.set("users", file);
    return axiosInstance.post('/user/import/support', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getAlertPlusTemplates: function () {
    return axiosInstance.get(`/aplus/templates`)
  },
  getTemplate: function (id) {
    return axiosInstance.get(`/aplus/template?id=${id}`)
  },
  uploadTemplate: function (token, name, duration, preset, sound, icon, image) {
    let formData = new FormData();
    if (token) {
      formData.set("token", token);
    }
    formData.set("name", name);
    if (icon) formData.set("icon", icon);
    formData.set("duration", duration);
    formData.set("preset", preset);
    formData.set("sound", sound);
    if (image) formData.set("image", image);
    return axiosInstance.post('/aplus/template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
    });
  },
  updateTemplate: function (id, token, name, duration, preset, sound, icon, image) {
    let formData = new FormData();
    formData.set("id", id);
    if (token) {
      formData.set("token", token);
    }
    formData.set("name", name);
    formData.set("icon", icon);
    formData.set("duration", duration);
    formData.set("preset", preset);
    formData.set("sound", sound);
    formData.set("image", image);
    return axiosInstance.put(`/aplus/template?id=${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
    });
  },
  deleteAPlusTemplate: function (templateIds, token) {
    /*return axiosInstance.delete('/aplus/template', {data: {id: templateIds, token}});*/
    return axiosInstance.delete(`/aplus/template?id=${templateIds}`, { data: { id: templateIds, token } });
  },
  editRxExpiration: function (macs, datetime) {
    return axiosInstance.put(`/receiver/expiration`, {
      macs, datetime
    });
  },
  deleteRx: function (macs) {
    return axiosInstance.delete(`/receiver`, { data: { macs }, timeout: 30000 })
  },
  verifyUser: function (email, password, passcode) {
    console.log(email, passcode, password);
    return axiosInstance.post(`/user/verify`, {
      password, email, passcode
    })
  },
  // ACCOUNTS
  getAccounts: function () {
    return axiosInstance.get('/account/')
  },
  editAccount: function (accountInfo) {
    return axiosInstance.put('/account/', accountInfo)
  },
  deleteAccount: function (accountIds) {
    console.log('API deleteAccount accountIds:', accountIds);
    return axiosInstance.delete('/account', { data: { ids: accountIds } });
  },
  // API KEY
  generateApiKey: function (email, userId) {
    return axiosInstance.post('/api-key/generate', { email, userId })
  },
  getApiKey: function () {
    return axiosInstance.get('/api-key/latest')
  },
  // SITE MAPPING
  assignBuilding: function (buildingId, siteIds, cmsId) {
    return axiosInstance.post('/aplus/site-building', {
      buildingId,
      siteIds,
      cmsId
    })
  },
  //messenger
  getGroupsTree: function () {
    return axiosInstance.get('/messenger/groups/tree')
  },
  createMessage: function (message) {
    return axiosInstance.post('/messenger', message)
      .then(response => {
        console.log('create Message response:', response)
        return response
      })
      .catch(error => {
        console.log('create Message error:', error)
        throw error
      })
  },
  getqueuedMessages: function () {
    return axiosInstance.get('/messenger/queued')
  },
  stopMessages: function (id) {
    return axiosInstance.put(`/messenger/messages/task/${id}`)
  },
  deleteMessages: function (id) {
    return axiosInstance.delete('/messenger', { data: { id } });
  },
  getScheduledMessages: function () {
    return axiosInstance.get('/messenger/scheduled')
  },
  getHistories: function (limit) {
    return axiosInstance.get('/messenger/histories', { params: { limit } })
  },
  getMessagesId: function (id) {
    return axiosInstance.get(`/messenger/messages/${id}`)
  },
  updateMessage: function (message) {
    return axiosInstance.put('/messenger', message)
  },
  getMessengerReceivers: function (ids) {
    return axiosInstance.get('/messenger/receivers', { params: { ids } })
  },
  getGroups: function () {
    return axiosInstance.get('/messenger/groups')
  },
  updateGroup: function (group) {
    return axiosInstance.put('/messenger/groups', group)
  },
  deleteGroup: function (id) {
    return axiosInstance.delete('/messenger/groups', { data: { id } })
  },
  insertGroup: function (group) {
    return axiosInstance.post('/messenger/groups', group)
  },
  bindGroupsReceivers: function (groupIds, macs) {
    return axiosInstance.post('/messenger/groups/bind', { groupIds, macs });
  },
  unbindGroupsReceivers: function (groupIds, macs) {
    return axiosInstance.post('/messenger/groups/unbind', { groupIds, macs });
  },
  // userInfo contains email, firstName, lastName, and phone
  addMessengerUser: function (userInfo) {
    return axiosInstance.post("/messenger/user", userInfo)
      .then(response => {
        console.log('API addUser response:', response)
        return response
      })
      .catch(error => {
        console.log('API addUser error:', error)
        throw error
      })
  },
  deleteMessengerUser: function (id) {
    return axiosInstance.delete('/messenger/user', {
      data: { id }
    });
  },
  importMessengerUsers: function (file) {
    let formData = new FormData();
    formData.set("users", file);
    return axiosInstance.post('/messenger/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getMessengerUsers: function () {
    return axiosInstance.get('/messenger/users')
  },
  deassignBuilding: function(siteIds, cmsId) {
    return axiosInstance.delete('/aplus/site-building', {
      data: {
        siteIds,
        cmsId
      }
    })
  },

  //Signage Plus
  getSignagePlusSites: function () {
    return axiosInstance.get('/signage/getSitesByUserId')
  },
  getMedias: function () {
    return axiosInstance.get('/signage/medias')
  },
  insertMediaFolder: function (media) {
    return axiosInstance.post('/signage/media/folder', media)
  },
  updateMediaFolder: function (id, media) {
    return axiosInstance.put(`/signage/media/folder/?id=${id}`, media)
  },
  deleteMediaFolder: function (id) {
    return axiosInstance.delete(`/signage/media/folder/?id=${id}`);
  },
  updateMediaFile: function (id, media) {
    return axiosInstance.put(`/signage/media/folder/?id=${id}`, media)
  },
  uploadMediaFiles: function (parentId, filesObjs) {   
    let formData = new FormData();
    formData.set("parentId", parentId);
    filesObjs.forEach((file) => {
      formData.append("medias[]", file);
    });
    return axiosInstance.post('/signage/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60 * 10 * 1000 // 10 minutes timeout for large file uploads
    });
  },
  moveMediaFiles: function (ids) {
    return axiosInstance.put('/signage/media/file/move', { data: ids })
  },
  deleteMediaFiles: function (delFiles) {
    return axiosInstance.delete('/signage/media/file', { data: delFiles })
  },
  getCapacityBySite: function (siteId, cmsId) {
    return axiosInstance.get('/signage/capacity', { params: { siteId, cmsId } })
  },
  getPlaylists: function () {
    return axiosInstance.get('/signage/playlists')
  },
  addPlaylist: function (playlist) {
    return axiosInstance.post('/signage/playlists/add', playlist)
  },
  updatePlaylist: function (playlist) {
    return axiosInstance.put('/signage/playlists/update', playlist)
  },
  deletePlaylists: function (playlistId) {
    return axiosInstance.delete('/signage/playlists/delete', { data: { playlistId } })
  },
  getPlaylistsById: function (playlistId) {
    return axiosInstance.get(`/signage/playlists/${playlistId}`)
  },
  getPlaylistItems: function (playlistId) {
    return axiosInstance.get(`/signage/playlist/item/${playlistId}`)
  },
  getMediasByCategory: function (category) {
    return axiosInstance.get('/signage/medias/category', { params: { category } })
  },
  updatePlaylistItem: function (id, data) {
    return axiosInstance.put('/signage/playlist/item/update', { id, data }, {
      timeout: 1000*2*60
    })
  },
  addMedia: function (data) {
    return axiosInstance.post('/signage/medias/add', data)
  },
  updateMedia: function (data) {
    return axiosInstance.put('/signage/medias/update', data)
  },
  deleteMedia: function (id) {
    return axiosInstance.delete(`/signage/medias/delete?id=${id}`)
  },
  getAllSignageStatus: function () {
    return axiosInstance.get('/signage/status')
  },
  getAllScheduledSignages: function () {
    return axiosInstance.get('/signage/schedule')
  },
  stopSignage: function (id) {
    return axiosInstance.put(`/signage/stop?id=${id}`)
  },
  getSignageHistory: function () {
    return axiosInstance.get('/signage/logs', { params: { limit: 50 } })
  },
  getGroupReceiverByUser: function () {
    return axiosInstance.get(`/signage/group/receiver`)
  },
  getSignages: function(status) {
    return axiosInstance.get(`/signage/signages`, { params: { status } })
  },
  addDrafts: function (data) {
    return axiosInstance.post('/signage/drafts/add', data)
  },
  updateDrafts: function (id,data) {
    return axiosInstance.put(`/signage/drafts/update`,  { id, data })
  },
  deleteSignages: function (ids) {
    return axiosInstance.delete(`/signage/signages`, { data: ids })
  },
  addSignage: function (data) {
    return axiosInstance.post('/signage/add', data)
  },
  deleteSignage: function (id) {
    return axiosInstance.delete(`/signage/delete?id=${id}`)
  },
  updateSignage: function (id,data) {
    return axiosInstance.put(`/signage/update`,  { id, data })
  },
  checkSignageConflict: function (id, data) {
    return axiosInstance.post(`/signage/check/conflict`, { id, data })
  },
  getSignageById: function (id) {
    return axiosInstance.get(`/signage/get/${id}`)
  },
  // Add this function to get users assigned to a specific group
  getUsersInGroup: function (groupId) {
    console.log(`Calling API getUsersInGroup for group: ${groupId}`);
    return axiosInstance.get(`/messenger/groups/${groupId}/users`);
  },

  getUsersByReceiver: function (receiverId) {
    console.log(`Calling API getUsersByReceiver for receiver: ${receiverId}`);
    return axiosInstance.get(`/messenger/receivers/${receiverId}/users`);
  },

  /**
   * Bind users to groups
   * @param {Array} userIds - Array of user IDs
   * @param {Array} groupIds - Array of group IDs
   * @returns {Promise} - Promise resolving to the API response
   */
  bindUsersToGroups: (userIds, groupIds) => {
    return axiosInstance.post('/messenger/users-groups', { userIds, groupIds });
  },

  /**
   * Unbind all users from specified groups
   * @param {Array|String} groupIds - Array of group IDs or single group ID
   * @returns {Promise} - Promise resolving to the API response
   */
  unbindUsersFromGroups: function(groupIds) {
    return axiosInstance.delete('/messenger/users-groups', { 
      data: { groupIds } 
    });
  },

  // Signage Plus
  getSignageUsers: function () {
    console.log('Calling API getSignagePlusUsers');
    return axiosInstance.get('/signage/users');
  },
  getDashboardReceivers: function() {
    return axiosInstance.get('/signage/dashboard/receivers');
  },
  getSignageReceivers: function(ids) {
    return axiosInstance.get('/signage/receivers', { params: { ids } });
  },
  getSignageGroups: function() {
    return axiosInstance.get('/signage/groups');
  },
  getSignageGroupReceivers: function(groupIds) {
    return axiosInstance.get('/signage/receivers', { params: { groupIds } });
  },
  deleteSignageGroup: function(groupId) {
    return axiosInstance.delete(`/signage/groups/${groupId}`);
  },
  getSignageDashboardSummary: function(portal) {
    return axiosInstance.get('/signage/dashboard-summary', { params: { portal: portal? portal: 'admin' } });
  },
  getSignageReceiverStatuses: function() {
    return axiosInstance.get('/signage/receiver-statuses');
  },
  bindSignageGroupsReceivers: function (groupIds, macs) {
    return axiosInstance.post('/signage/groups/bind', { groupIds, macs });
  },
  unbindSignageGroupsReceivers: function (groupIds, macs) {
    return axiosInstance.post('/signage/groups/unbind', { groupIds, macs });
  },
  insertSignageGroup: function (group) {
    return axiosInstance.post('/signage/groups', group);
  },
  updateSignageGroup: function (group) {
    return axiosInstance.put('/signage/groups', group);
  },
  bindSignageUsersToGroups: (userIds, groupIds) => {
    return axiosInstance.post('/signage/users-groups', { userIds, groupIds });
  },
  bindSignageGroupToSites: (groupId, cmsId) => {
    return axiosInstance.post(`/signage/groups/${groupId}/sites`, { cmsId });
  },
  unbindSignageUsersFromGroups: function(userIds, groupIds) {
    return axiosInstance.delete('/signage/users-groups', { 
      data: { userIds, groupIds } 
    });
  },
  getSignageUsersByReceiver: function (receiverId) {
    console.log(`Calling API getSignageUsersByReceiver for receiver: ${receiverId}`);
    return axiosInstance.get(`/signage/receivers/${receiverId}/users`);
  },

  getSignageUsersInGroup: function (groupId) {
    console.log(`Calling API getSignageUsersInGroup for group: ${groupId}`);
    return axiosInstance.get(`/signage/groups/${groupId}/users`);
  },
  getSignageHistories: function (limit) {
    console.log(`Calling API getSignageHistories with limit: ${limit}`);
    return axiosInstance.get('/signage/histories', { params: { limit } });
  },

  // Signage Plus Control
  playSignagesByMacs: function (macs) {
    console.log(`Calling API playSignagesByMacs for receivers: ${macs.join(', ')}`);
    return axiosInstance.post('/signage/signages/play', { macs });
  },

  pauseSignagesByMacs: function (macs) {
    console.log(`Calling API pauseSignagesByMacs for receivers: ${macs.join(', ')}`);
    return axiosInstance.post('/signage/signages/pause', { macs });
  },

  stopSignagesByMacs: function (macs) {
    console.log(`Calling API stopSignagesByMacs for receivers: ${macs.join(', ')}`);
    return axiosInstance.post('/signage/signages/stop', { macs });
  },

  addSignageUser: function (userInfo) {
    console.log('userInfo', userInfo);
    return axiosInstance.post("/signage/user", userInfo)
      .then(response => {
        console.log('API addUser response:', response)
        return response
      })
      .catch(error => {
        console.log('API addUser error:', error)
        throw error
      })
  },
  deleteSignageUser: function (id) {
    return axiosInstance.delete('/signage/user', {
      data: { id }
    });
  },
  importSignageUsers: function (file) {
    let formData = new FormData();
    formData.set("users", file);
    return axiosInstance.post('/signage/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  //Signage Canva Integration
  CanvaAuthenticate: function () { return axiosInstance.get('/canva/authenticate') }, 
  CanvaSignout: function () { return axiosInstance.get('/canva/oauth-signout')},
  getCanvaFolders: function () { return axiosInstance.get('/canva/folders') },
  getCanvaAuthenticateUrl: function () {return axiosInstance.get('/canva/oauth-url')},
  postCanvaToken: function(code) {
    return axiosInstance.post('/canva/oauth-redirect', {code: code})},

  // Singlewire API methods
  getSinglewireApiKey: function (accountId) {
    console.log('Calling API getSinglewireApiKey for account:', accountId);
    return axiosInstance.get(`/singlewire/api-key/${accountId}`);
  },

  generateSinglewireApiKey: function (email, userId) {
    console.log('Calling API generateSinglewireApiKey for user:', userId);
    return axiosInstance.post('/singlewire/api-key/generate', { email, userId });
  },

  getSinglewireToken: function () {
    console.log('Calling API getSinglewireToken');
    return axiosInstance.get('/singlewire/token');
  },

  disconnectSinglewire: function () {
    console.log('Calling API disconnectSinglewire');
    return axiosInstance.delete('/singlewire/disconnect');
  },

  storeSinglewireToken: function (token) {
    console.log('Calling API storeSinglewireToken');
    return axiosInstance.post('/singlewire/store-token', { token });
  },

  getSinglewireSites: function () {
    console.log('Calling API getSinglewireSites');
    return axiosInstance.get('/singlewire/sites');
  },

  getSinglewireSiteRoles: function () {
    console.log('Calling API getSinglewireSiteRoles');
    return axiosInstance.get('/singlewire/site-roles');
  },

  getSinglewireSiteRoleSites: function (siteRoleId) {
    console.log('Calling API getSinglewireSiteRoleSites for siteRoleId:', siteRoleId);
    return axiosInstance.get(`/singlewire/site-role-sites/${siteRoleId}`);
  },

  assignSinglewireSite: function (singlewireSiteName, siteIds, cmsId) {
    console.log('Calling API assignSinglewireSite');
    return axiosInstance.post('/singlewire/assign-site', { singlewireSiteName, siteIds, cmsId });
  },

  deassignSinglewireSite: function (siteIds, cmsId) {
    console.log('Calling API deassignSinglewireSite');
    return axiosInstance.delete('/singlewire/deassign-site', { data: { siteIds, cmsId } });
  },

  getSinglewireAlerts: function () {
    console.log('Calling API getSinglewireAlerts');
    return axiosInstance.get('/singlewire/alerts');
  },

  // Raptor API methods  -- for /raptor route 
  getRaptorApiKey: function () {
    console.log('Calling API getRaptorApiKey');
    return axiosInstance.get('/raptor/api-key');
  },

  generateRaptorApiKey: function (email, userId) {
    console.log('Calling API generateRaptorApiKey for user:', userId);
    return axiosInstance.post('/raptor/api-key/generate', { email, userId });
  },

  // getRaptorClientUID: function () {
  //   console.log('Calling API getRaptorClientUID');
  //   return axiosInstance.get('/raptor/client-uid');
  // },

  // updateRaptorClientUID: function (clientUID) {
  //   console.log('Calling API updateRaptorClientUID');
  //   return axiosInstance.put('/raptor/client-uid', { clientUID });
  // },

  // Raptor building assignment methods
  // assignRaptorBuilding: function (buildingId, siteIds, cmsId) {
  //   console.log('Calling API assignRaptorBuilding');
  //   return axiosInstance.post('/raptor/assign-building', {
  //     buildingId,
  //     siteIds,
  //     cmsId
  //   });
  // },

  // deassignRaptorBuilding: function (siteIds, cmsId) {
  //   console.log('Calling API deassignRaptorBuilding');
  //   return axiosInstance.delete('/raptor/deassign-building', {
  //     data: {
  //       siteIds,
  //       cmsId
  //     }
  //   });
  // },

  // getRaptorAlerts: function () {
  //   console.log('Calling API getRaptorAlerts');
  //   return axiosInstance.get('/raptor/alerts');
  // },

  // getRaptorBuildings: function (clientUID) {
  //   console.log('Calling API getRaptorBuildings for client:', clientUID);
  //   return axiosInstance.get(`/raptor/buildings/${clientUID}`);
  // }

  // TRIAL PROVISIONING
  validateAccountOwner: function (email) {
    console.log('Calling API validateAccountOwner with email:', email);
    return axiosInstance.post('/trial/validate-account-owner', { email });
  },
  
  provisionTrialAccounts: function (userInfo) {
    console.log('Calling API provisionTrialAccounts with data:', userInfo);
    return axiosInstance.post('/trial/provision', userInfo);
  },
  
  unprovisionTrialAccounts: function (criteria) {
    console.log('Calling API unprovisionTrialAccounts with criteria:', criteria);
    return axiosInstance.post('/trial/unprovision', criteria);
  }
}

export default API
